import { env } from "@/env";

const currentHost = window.location.host;

export const getTournamentUrl = (): string =>
  env.domainManagerMap[currentHost] ?? env.tournamentUrl;

/** Internal route that mints a handoff token and forwards to the Manager app. */
export const MANAGER_REDIRECT_PATH = "/manager-redirect";

/**
 * Every Manager origin this Admin deploy is allowed to forward to: the
 * configured Manager URL plus every white-label override — i.e. exactly the set
 * {@link getTournamentUrl} can return. Used to reject an attacker-supplied
 * `?to=` on {@link MANAGER_REDIRECT_PATH} (open-redirect guard).
 */
const getAllowedManagerOrigins = (): string[] =>
  [env.tournamentUrl, ...Object.values(env.domainManagerMap)].reduce<string[]>((acc, url) => {
    try {
      const { origin } = new URL(url);
      return acc.includes(origin) ? acc : [...acc, origin];
    } catch {
      return acc;
    }
  }, []);

/**
 * Validates a `?to=` target for {@link MANAGER_REDIRECT_PATH}: it must be an
 * absolute URL on one of the configured Manager origins.
 */
export const isAllowedManagerUrl = (url: string | null): boolean => {
  if (!url) return false;

  try {
    return getAllowedManagerOrigins().includes(new URL(url).origin);
  } catch {
    return false;
  }
};

const withQueryParam = (url: string, key: string, value: string): string =>
  `${url}${url.includes("?") ? "&" : "?"}${key}=${value}`;

/** Appends the legacy `?token=` handoff, the way every Manager link used to. */
export const appendLegacyTokenParam = (url: string, token: string | null): string =>
  token ? withQueryParam(url, "token", token) : url;

/**
 * Builds a link into the Manager app for a Manager-relative `path`
 * (e.g. `/dashboard/123/general-settings`, query string allowed).
 *
 * Always returns a same-app `/manager-redirect?to=<encoded absolute URL>` link.
 * That route mints a single-use handoff token and forwards, so the access token
 * never appears in a URL. Safe in `window.open(..., "_blank")` too: the new tab
 * opens on Admin and then replaces itself with the Manager URL.
 */
export const getManagerHref = (path: string) => {
  const target = `${getTournamentUrl()}${path}`;

  return `${MANAGER_REDIRECT_PATH}?to=${encodeURIComponent(target)}`;
};

/**
 * Builds the Manager link for a tournament's own page (its general settings).
 *
 * Admin has no tournament detail page, so every "open this tournament" link
 * points at Manager. Clicked links must open in a new tab (`target="_blank"` /
 * `window.open(href, "_blank")`); the one exception is the route-level handoff
 * in `tournamentRouteRedirects.ts`, where there is no originating tab to keep.
 * The returned href is the same-app `/manager-redirect?to=…` hop produced by
 * {@link getManagerHref}, so it must NOT be passed through `useScopedHref`.
 *
 * ## Manager deep links are numeric-only — never pass a public id
 *
 * `internalId` is required, and deliberately so. Manager's tournament routes
 * parse the path segment with `parseInt` and gate the whole `/dashboard/*` tree
 * on `!isNaN(Number(segment))`; the underlying query is
 * `tournamentDetailById($id: Int!)`, with no publicId overload. A `trn_…` id
 * therefore does **not** 404 — it fails **silently and destructively**: Manager
 * redirects to whatever tournament the user last had selected, or, with none,
 * opens its create form so a submit would make a *duplicate* tournament.
 *
 * So never reintroduce an `internalId ?? id` fallback here thinking it degrades
 * gracefully. A caller that only has the public id must link to the surviving
 * Admin page (`/tournament/:id/matches`) instead, or resolve the numeric id
 * first — see `redirectTournamentRootToManager`.
 */
export const getTournamentManagerHref = (internalId: number): string =>
  getManagerHref(`/dashboard/${internalId}/general-settings`);
